import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Flash } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formCats } from "./flashTheme";

const GRID = "grid grid-cols-[minmax(160px,1fr)_repeat(4,96px)_56px] gap-2 items-end";
const num = (v) => (v === "" || v === undefined || v === null ? null : Number(v));
const monthEnd = (y, m) => new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10);
const pastMonths = (iso) => {
  const y = Number(iso.slice(0, 4)), m = Number(iso.slice(5, 7)) - 1;
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    return { value: monthEnd(d.getUTCFullYear(), d.getUTCMonth()), label: d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }) };
  });
};
const autoLast = (cats, row) => {
  if (cats.length < 2) return null;
  const others = cats.slice(0, -1).map((c) => num(row[c.key]));
  return others.every((v) => v !== null) ? Math.max(0, +(100 - others.reduce((a, v) => a + v, 0)).toFixed(2)) : null;
};
const rowValues = (cats, row) => {
  const out = {};
  cats.forEach((c, i) => { out[c.key] = num(row[c.key]) ?? (i === cats.length - 1 ? autoLast(cats, row) : null); });
  return out;
};

const EntryRow = ({ form, row, onChange }) => {
  const cats = formCats(form);
  const vals = rowValues(cats, row);
  const filled = vals[cats[0].key] !== null && vals[cats[0].key] !== undefined;
  const sum = Object.values(vals).reduce((a, v) => a + (v ?? 0), 0);
  const ok = cats.length === 1 || (sum >= 98 && sum <= 102);
  return (
    <div className={`${GRID} py-2.5 border-b border-slate-100`} data-testid={`entry-row-${form.id}`}>
      <div className="min-w-0 pb-1.5">
        <div className="font-medium text-sm text-slate-900 truncate">
          {form.name}
          {form.hidden && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded px-1">Hidden</span>}
        </div>
        <div className="text-[11px] text-slate-500 truncate">{form.standard}{form.owner ? ` · ${form.owner}` : ""}</div>
      </div>
      {[0, 1, 2, 3].map((i) => {
        const c = cats[i];
        if (!c) return <span key={i} />;
        const last = i === cats.length - 1 && cats.length > 1;
        return (
          <label key={c.key} className="space-y-1 min-w-0">
            <span className={`block text-[10px] uppercase tracking-wider font-semibold truncate ${i === 0 ? "text-emerald-700" : "text-slate-500"}`} title={c.label}>{c.label}</span>
            <Input
              type="number" step="0.01" min="0" max="100"
              value={row[c.key] ?? ""}
              placeholder={last && autoLast(cats, row) !== null ? String(autoLast(cats, row)) : "—"}
              onChange={(e) => onChange(c.key, e.target.value)}
              className="h-9 font-mono text-sm"
              data-testid={`entry-${c.key}-${form.id}`}
            />
          </label>
        );
      })}
      <span className={`pb-2.5 text-xs font-mono text-right ${!filled ? "text-slate-300" : ok ? "text-emerald-600" : "text-rose-600"}`} data-testid={`entry-sum-${form.id}`}>
        {filled && cats.length > 1 ? `${sum.toFixed(1)}%` : "—"}
      </span>
    </div>
  );
};

export const FlashEntryDialog = ({ open, onOpenChange, initialDate, forms, onSaved }) => {
  const [date, setDate] = useState(initialDate);
  const [rows, setRows] = useState({});
  const [existing, setExisting] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const months = useMemo(() => pastMonths(initialDate), [initialDate]);

  useEffect(() => { if (open) setDate(initialDate); }, [open, initialDate]);
  useEffect(() => {
    if (!open || !date) return;
    Flash.entries(date).then((list) => {
      const map = {};
      list.forEach((e) => { map[e.form_id] = { ...e.values }; });
      setRows(map);
      setExisting(new Set(list.map((e) => e.form_id)));
    });
  }, [open, date]);

  const setField = (id, k, v) => setRows((r) => ({ ...r, [id]: { ...(r[id] || {}), [k]: v } }));
  const isMonthClose = months.some((m) => m.value === date);

  const save = async () => {
    const items = forms
      .filter((f) => existing.has(f.id) || formCats(f).some((c) => num(rows[f.id]?.[c.key]) !== null))
      .map((f) => ({ form_id: f.id, values: rowValues(formCats(f), rows[f.id] || {}) }));
    setSaving(true);
    try {
      const res = await Flash.saveEntries({ date, items });
      toast.success(`Saved ${res.saved} form${res.saved === 1 ? "" : "s"} for ${date}`);
      onSaved(date);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save metrics");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white" data-testid="data-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Enter daily flash metrics</DialogTitle>
          <DialogDescription>
            Type each form's percentages from HCS Analytics. The first (green) category is scored against the goal. Leave the last box blank to auto-fill the remainder.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 font-mono" data-testid="entry-date-input" />
          <Button variant={date === initialDate ? "default" : "outline"} size="sm" onClick={() => setDate(initialDate)} className={date === initialDate ? "bg-[#0284c7] hover:bg-[#0369a1]" : ""} data-testid="entry-date-report">
            Month-to-date
          </Button>
          <select
            value={isMonthClose ? date : ""}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className={`h-9 rounded-md border px-3 text-sm bg-white ${isMonthClose ? "border-sky-400 text-sky-800 font-medium" : "border-slate-200 text-slate-700"}`}
            data-testid="entry-month-close-select"
          >
            <option value="">Previous month close…</option>
            {months.map((m) => <option key={m.value} value={m.value}>{m.label} (final)</option>)}
          </select>
        </div>
        <div className="max-h-[52vh] overflow-y-auto">
          {forms.map((f) => <EntryRow key={f.id} form={f} row={rows[f.id] || {}} onChange={(k, v) => setField(f.id, k, v)} />)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0284c7] hover:bg-[#0369a1]" data-testid="data-entry-submit-button">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save metrics
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
